// The icons the panel draws itself rather than taking from lucide. The design
// takes its icons from Hugeicons' stroke-rounded set
// (https://hugeicons.com/icons/stroke-rounded), so a new icon comes from
// there: paste the paths in as a component here, stroked with `currentColor`,
// rather than reaching for the nearest lucide lookalike. The delete bin and
// the edit pencil were traced from the meal plan assets in
// `src/assets/svg/meal plan`, which are that set's `delete-02` and
// `pencil-edit-02`.
//
// Each asset ships an active and an inactive copy, and the two are the same
// outline — only the stroke colour differs (the pencil dark on a pale pill and
// pale on a filled one; the bin red when it will delete and grey when it will
// not). So there is one component per icon here, stroked with `currentColor`,
// and the state is whatever colour the call site is already setting on the
// button. Each keeps the viewBox and stroke width of the file it came from, so
// the weight is the designed one at whatever size the class asks for.

import React from "react";

export type IconProps = React.SVGProps<SVGSVGElement>;

const baseProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  "aria-hidden": true,
} as const;

/** A lidded bin: handle, lid, tapered body, two ribs. */
export const TrashIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 20 20"
    strokeWidth={1.25}
    className={className}
    {...props}
  >
    <path d="M16.25 4.58398L15.7336 12.9382C15.6016 15.0727 15.5357 16.1399 15.0007 16.9072C14.7361 17.2866 14.3956 17.6067 14.0006 17.8473C13.2017 18.334 12.1325 18.334 9.99392 18.334C7.8526 18.334 6.78192 18.334 5.98254 17.8464C5.58733 17.6054 5.24667 17.2847 4.98223 16.9047C4.4474 16.1362 4.38287 15.0674 4.25384 12.93L3.75 4.58398" />
    <path d="M2.5 4.58268H17.5M13.3797 4.58268L12.8109 3.40912C12.433 2.62957 12.244 2.23978 11.9181 1.99669C11.8458 1.94277 11.7693 1.8948 11.6892 1.85327C11.3283 1.66602 10.8951 1.66602 10.0287 1.66602C9.14067 1.66602 8.69667 1.66602 8.32973 1.86112C8.24842 1.90436 8.17082 1.95427 8.09774 2.01032C7.76803 2.26327 7.58386 2.66731 7.21551 3.4754L6.71077 4.58268" />
    <path d="M7.91699 13.75V8.75" />
    <path d="M12.083 13.75V8.75" />
  </svg>
);

/** A pencil lying on its diagonal, nib to the bottom left. */
export const PencilIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 18 18"
    strokeWidth={1}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M4.43434 11.894L4 14.5L6.60603 14.0657C7.05858 13.9903 7.47625 13.7753 7.80066 13.4509L13.6777 7.57382C14.1075 7.14401 14.1075 6.44717 13.6776 6.01737L12.4826 4.82235C12.0528 4.39254 11.3559 4.39255 10.9261 4.82237L5.04912 10.6994C4.72471 11.0238 4.50976 11.4415 4.43434 11.894Z" />
    <path d="M10.1113 6.16699L12.3336 8.38922" />
  </svg>
);

/**
 * A padlock, the Hugeicons stroke-rounded `square-lock-01` — the icon the
 * design uses — with `open` swapping in `square-unlock-01`. The two share the
 * body and keyhole; only the shackle differs, swung up and out to the right
 * once undone. Traced rather than taken from a package, like the bin and
 * pencil above, so it is stroked with `currentColor` at the design's weight.
 */
export const LockIcon: React.FC<IconProps & { open?: boolean }> = ({
  open = false,
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M12 14V17.0023" />
    {open ? (
      <path d="M7.5 9V6.5C7.5 4.01472 9.51472 2 12 2C13.9593 2 15.8664 3.25221 16.5 5" />
    ) : (
      <path d="M16.5 9V6.5C16.5 4.01472 14.4853 2 12 2C9.51472 2 7.5 4.01472 7.5 6.5V9" />
    )}
    <path d="M4.26781 18.8447C4.49269 20.515 5.87613 21.8235 7.55966 21.9009C8.97627 21.966 10.4153 22 12 22C13.5847 22 15.0237 21.966 16.4403 21.9009C18.1239 21.8235 19.5073 20.515 19.7322 18.8447C19.879 17.7547 20 16.6376 20 15.5C20 14.3624 19.879 13.2453 19.7322 12.1553C19.5073 10.485 18.1239 9.17649 16.4403 9.09909C15.0237 9.03397 13.5847 9 12 9C10.4153 9 8.97627 9.03397 7.55966 9.09909C5.87613 9.17649 4.49269 10.485 4.26781 12.1553C4.12104 13.2453 4 14.3624 4 15.5C4 16.6376 4.12104 17.7547 4.26781 18.8447Z" />
  </svg>
);
